import Image from "../gui/Image";
import Modal from "../gui/Modal";
import AbstractDataHandler from "../shared/AbstractDataHandler";
import { CURRENT_VERSION } from "../shared/constants";
import { getBaseUrl, getPlanPath, getPlanSlug, getUsername } from "../shared/utils";

export default class DataHandler extends AbstractDataHandler {
    redirectAfterSave = false;

    getData() {
        if (this.isNewPlan()) {
            let storageData = this.storage.get();
            let newData = this.loadData(storageData, storageData);
            storageData == null
                ? this.provider.showWelcomeMessage()
                : (
                    newData.showMigrationMessage
                        ? this.provider.showMigrationMessage() : null
                )
            this.provider._checkNewPlanPrompts(false);
        } else {
            let uri = `${getBaseUrl(this.p)}/${getPlanSlug(this.p)}/data`;
            this.spinner.showFullscreen();
            this.executeGetRequest(uri, (data) => {
                let newData = this.loadData(data, this.storage.get());
                this.imageMenu.opacitySlider.value(newData.image?.opacity || 255);
                if (newData.showMigrationMessage) {
                    this.provider.showMigrationMessage();
                }
            });
        }
    }

    loadLists(func) {
        let uri = `/${username}/plans/lists`;
        this.spinner.showSidebar();
        this.executeGetRequest(uri, func);
    }

    savePlan(forced = false) {
        if (!forced && !this.unsavedChanges) {
            return;
        }

        if (!this.isNewPlan()) {
            this.updatePlan();
        } else if (!username) {
            location.href = this.p.getURL().replace('/plans/new', '/users/sign_in');
        } else if (!this.settings.name) {
            this.askForName();
        } else {
            this.createPlan();
        }
    }

    askForName(onDismiss = null) {
        this.enableHotKeys = false;
        let nameModal = new Modal(this.provider);
        let nameInput = this.p.createInput('').class('input-text');
        let clickAction = () => {
            this.settings.name = nameInput.value();
            nameModal.remove();
            this.createPlan();
        }
        nameInput.elt.addEventListener('keypress', (event) => {
            if (event.key === 'Enter') {
                clickAction();
            }
        });
        nameModal.onClickOutside(() => {
            this.enableHotKeys = true;
            nameModal.remove();
            onDismiss?.();
        })
            .setTitle('Add a name')
            .addContent(nameInput)
            .setButtonText('Save')
            .onClickButton(clickAction);
    }

    askToSavePrompt(onDismiss = null) {
        this.enableHotKeys = false;
        let modal = new Modal(this.provider);
        let nameInput = this.p.createInput('').class('input-text');
        let dismiss = () => {
            this.enableHotKeys = true;
            modal.remove();
            onDismiss?.();
        };
        let save = () => {
            this.settings.name = nameInput.value();
            modal.remove();
            this.createPlan();
        };
        nameInput.elt.addEventListener('keypress', (event) => {
            if (event.key === 'Enter') save();
        });
        modal.onClickOutside(dismiss)
            .setTitle('Save your plan?')
            .addContent(this.p.createElement('p', 'Give it a name and we\'ll save it for you.'))
            .addContent(nameInput)
            .setButtonText('Save')
            .onClickButton(save)
            .onClickCancel(dismiss);
        modal.cancelButton.html('Not now');
    }

    createPlan(name) {
        let postData = this.getPostData();
        this.createSpinner();
        this.executeSaveRequest(
            '/plans',
            'POST',
            postData,
            (result) => {
                location.href = this.p.getURL().replace('/plans/new', result['uri']);
            });
    }

    updatePlan() {
        let uri = getPlanPath(this.p);
        this.redirectAfterSave = this.settings.name != this.planName;
        if (this.redirectAfterSave) {
            this.createSpinner();
        }
        this.executeSaveRequest(
            uri,
            'PATCH',
            this.getPostData(),
            (result) => {
                if (this.redirectAfterSave) {
                    location.href = this.p.getURL().replace(uri + '/edit', result['uri']);
                }
            });
    }

    deletePlan() {
        let uri = getPlanPath(this.p)
        this.createSpinner();
        this.p.httpDo(
            uri,
            {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': this.csrfToken,
                    'Accept': 'application/json',
                }
            },
            (data) => {
                location.href = this.p.getURL().replace(uri + '/edit', '/' + getUsername(this.p) + '/plans');
            },
            (data) => {
                console.log(JSON.parse(data));
            }
        )
    }

    createSpinner() {
        this.spinner.showFullscreen();
    }

    getPostData() {
        return {
            plan: {
                content: {
                    view: this.view.export(),
                    settings: this.settings.export(),
                    elements: this.elements.export(),
                    image: this.image?.export(),
                    version: CURRENT_VERSION,
                },
                name: this.settings.name,
                access: this.settings.access,
            }
        };
    }

    updateImage(file) {
        this.image = null;
        const formData = new FormData();
        formData.append('image[file]', file.file);

        let uri = getPlanPath(this.p) + '/image/background_image';

        fetch(uri, {
            method: 'POST',
            headers: {
                'X-CSRF-Token': this.csrfToken,
                'Accept': 'application/json',
            },
            body: formData,
        })
            .then(response => response.json())
            .then(data => {
                this.image = new Image(this.provider, data, true);
            })
            .catch(error => {
                console.error(error);
            });
    }

    uploadOverview(blob) {
        if (this.isNewPlan()) return;

        const formData = new FormData();
        formData.append('image[file]', blob, 'overview.png');

        let uri = getPlanPath(this.p) + '/image/overview';

        fetch(uri, {
            method: 'POST',
            headers: {
                'X-CSRF-Token': this.csrfToken,
                'Accept': 'application/json',
            },
            body: formData,
        })
            .catch(error => {
                console.error(error);
            });
    }

    deleteImage(id) {
        fetch(`/plans/image/${id}`, {
            method: 'DELETE',
            headers: {
                'X-CSRF-Token': this.csrfToken,
                'Accept': 'application/json',
            },
        })
            .catch(error => {
                console.error(error);
            });
    }


    isNewPlan() {
        return this.p.getURLPath()[1] == 'new';
    }
};
